/**
 * Music player core: a deterministic state machine with injectable timers and media, so every
 * race (ambient-break expiry, user pause, manual skip, seek, sleep timer, playlist change,
 * disposal) can be tested without a browser.
 *
 * Rules:
 * - When a track ends naturally and ambient breaks are on, wait a random 20–60 s ("Listening to
 *   the world…") before the next track. The environment level is untouched.
 * - Previous / Next / "Play next now" bypass the break.
 * - A user pause always stays paused; every pending timer is invalidated by a generation token.
 * - Turning breaks off during a break starts the next track immediately (only if playback was
 *   active, never over an explicit pause).
 * - repeat 'one' repeats the current track immediately with no break; repeat 'all' respects breaks;
 *   repeat 'off' stops after the last track.
 */

export interface TrackInfo {
  id: string;
  title: string;
  artist: string;
  url: string;
  source: string;
  license: string;
}

export type RepeatMode = 'off' | 'all' | 'one';
export type PlayerStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'break' | 'ended';

export interface Scheduler {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
  now(): number;
}

export interface MediaAdapter {
  load(url: string): void;
  play(): Promise<void>;
  pause(): void;
  seek(seconds: number): void;
  readonly currentTime: number;
  readonly duration: number;
  /** Set by the player. */
  onEnded: (() => void) | null;
  onError: (() => void) | null;
}

export interface PlayerSnapshot {
  status: PlayerStatus;
  track: TrackInfo | null;
  index: number;
  queue: TrackInfo[];
  /** Index in `queue` of the track that will play next (for "Up next"). */
  nextIndex: number;
  ambientBreaks: boolean;
  shuffle: boolean;
  repeat: RepeatMode;
  /** Wall time the current ambient break ends (informational only). */
  breakEndsAt: number | null;
  sleepEndsAt: number | null;
  sleepFading: boolean;
  sleepMusicOnly: boolean;
  error: string | null;
}

export interface PlayerHooks {
  /** Fade music/environment for the sleep timer (0 = silent, 1 = normal) over seconds. */
  fade?: (target: { music?: number; env?: number }, seconds: number) => void;
  /** Ask the host to pause environmental sound (sleep timer end without music-only). */
  onSleepComplete?: (musicOnly: boolean) => void;
  onChange?: (s: PlayerSnapshot) => void;
}

export const BREAK_MIN_MS = 20_000;
export const BREAK_MAX_MS = 60_000;
export const SLEEP_FADE_SECONDS = 45;

export class MusicPlayer {
  private tracks: TrackInfo[] = [];
  private order: number[] = [];
  private pos = 0;
  private status: PlayerStatus = 'idle';
  private gen = 0;
  private breakTimer: unknown = null;
  private breakEndsAt: number | null = null;
  private sleepTimer: unknown = null;
  private sleepFadeTimer: unknown = null;
  private sleepEndsAt: number | null = null;
  private sleepFading = false;
  private sleepMusicOnly = false;
  private ambientBreaks = true;
  private shuffle = false;
  private repeat: RepeatMode = 'all';
  private error: string | null = null;
  private disposed = false;
  /** Set when the listener paused during an ambient break: Play then starts the next track. */
  private pendingAdvance = false;

  constructor(
    private media: MediaAdapter,
    private sched: Scheduler,
    private random: () => number = Math.random,
    private hooks: PlayerHooks = {},
  ) {
    media.onEnded = () => this.handleEnded();
    media.onError = () => this.handleError();
  }

  // ----- configuration ---------------------------------------------------------------
  setPlaylist(tracks: TrackInfo[], opts: { keepCurrent?: boolean; autoplay?: boolean } = {}): void {
    const wasActive = this.status === 'playing' || this.status === 'loading' || this.status === 'break';
    if (opts.keepCurrent && this.currentTrack() && wasActive) {
      // Keep the current track; the new playlist follows after it.
      const current = this.currentTrack()!;
      this.tracks = [current, ...tracks.filter((t) => t.id !== current.id)];
      this.buildOrder(0);
      this.pos = 0;
      this.emit();
      return;
    }
    this.cancelBreak();
    this.tracks = tracks.slice();
    this.buildOrder(-1);
    this.pos = 0;
    this.error = null;
    if (wasActive || opts.autoplay) {
      this.startTrack();
    } else {
      this.status = this.status === 'paused' ? 'paused' : 'idle';
      if (this.status === 'paused') this.media.load(this.currentTrack()!.url);
      this.emit();
    }
  }

  setAmbientBreaks(on: boolean): void {
    this.ambientBreaks = on;
    if (!on && this.status === 'break') {
      // Playback was active (a user pause would have left 'break'): continue now.
      this.cancelBreak();
      this.advance(1, false);
      return;
    }
    this.emit();
  }

  setShuffle(on: boolean): void {
    if (on === this.shuffle) return;
    this.shuffle = on;
    const current = this.order[this.pos];
    this.buildOrder(current);
    this.pos = Math.max(0, this.order.indexOf(current));
    this.emit();
  }

  setRepeat(mode: RepeatMode): void {
    this.repeat = mode;
    this.emit();
  }

  private buildOrder(keepFirst: number): void {
    const n = this.tracks.length;
    const idx = Array.from({ length: n }, (_, i) => i);
    if (this.shuffle) {
      for (let i = n - 1; i > 0; i--) {
        const j = Math.floor(this.random() * (i + 1));
        [idx[i], idx[j]] = [idx[j], idx[i]];
      }
      if (keepFirst >= 0) {
        const at = idx.indexOf(keepFirst);
        idx.splice(at, 1);
        idx.unshift(keepFirst);
      }
    }
    this.order = idx;
  }

  // ----- transport -------------------------------------------------------------------
  play(): void {
    if (this.disposed || !this.tracks.length) return;
    this.restoreFromSleep();
    if (this.status === 'playing' || this.status === 'loading') return;
    if (this.status === 'paused' && this.pendingAdvance) {
      this.pendingAdvance = false;
      this.advance(1, false);
      return;
    }
    if (this.status === 'paused' && this.hasLoadedTrack) {
      this.status = 'playing';
      this.gen++;
      this.playMedia(this.gen);
      this.emit();
      return;
    }
    if (this.status === 'break' || this.status === 'ended') {
      // After a natural end, Play starts the next track straight away.
      this.cancelBreak();
      if (this.status === 'ended') this.pos = 0;
      else this.advanceIndex(1);
    }
    this.startTrack();
  }

  pause(): void {
    if (this.disposed) return;
    this.gen++;
    if (this.status === 'break') this.pendingAdvance = true;
    this.cancelBreak();
    this.media.pause();
    this.status = 'paused';
    this.emit();
  }

  toggle(): void {
    if (this.status === 'playing' || this.status === 'loading' || this.status === 'break') this.pause();
    else this.play();
  }

  next(): void {
    this.restoreFromSleep();
    this.cancelBreak();
    this.advance(1, true);
  }

  previous(): void {
    this.restoreFromSleep();
    this.cancelBreak();
    // Restart the current track if well into it; otherwise go back one.
    if ((this.status === 'playing' || this.status === 'paused') && this.media.currentTime > 4) {
      this.media.seek(0);
      if (this.status === 'paused') this.startTrack();
      this.emit();
      return;
    }
    this.advance(-1, true);
  }

  /** Skip the ambient break. */
  playNextNow(): void {
    if (this.status !== 'break') return;
    this.cancelBreak();
    this.advance(1, false);
  }

  selectTrack(trackIndexInQueue: number): void {
    if (trackIndexInQueue < 0 || trackIndexInQueue >= this.order.length) return;
    this.restoreFromSleep();
    this.cancelBreak();
    this.pendingAdvance = false;
    this.pos = trackIndexInQueue;
    this.startTrack();
  }

  seek(seconds: number): void {
    if (this.status !== 'playing' && this.status !== 'paused') return;
    const d = this.media.duration;
    const t = Math.max(0, Number.isFinite(d) && d > 0 ? Math.min(seconds, d - 0.25) : seconds);
    this.media.seek(t);
    this.emit();
  }

  // ----- sleep timer -------------------------------------------------------------------
  /** Start (minutes > 0) or cancel (null) the sleep timer. */
  setSleepTimer(minutes: number | null, musicOnly: boolean): void {
    this.clearSleep();
    this.restoreFromSleep();
    if (!minutes || minutes <= 0) {
      this.emit();
      return;
    }
    this.sleepMusicOnly = musicOnly;
    const g = this.gen;
    const ms = minutes * 60_000;
    this.sleepEndsAt = this.sched.now() + ms;
    const fadeStart = Math.max(0, ms - SLEEP_FADE_SECONDS * 1000);
    this.sleepFadeTimer = this.sched.setTimeout(() => {
      this.sleepFadeTimer = null;
      if (this.disposed) return;
      this.sleepFading = true;
      this.hooks.fade?.(musicOnly ? { music: 0 } : { music: 0, env: 0 }, Math.min(SLEEP_FADE_SECONDS, ms / 1000));
      this.emit();
    }, fadeStart);
    this.sleepTimer = this.sched.setTimeout(() => {
      this.sleepTimer = null;
      if (this.disposed) return;
      this.sleepEndsAt = null;
      this.sleepFading = false;
      // Pause music (a timer-initiated pause behaves exactly like a user pause).
      if (this.status === 'playing' || this.status === 'loading' || this.status === 'break') this.pause();
      this.hooks.onSleepComplete?.(musicOnly);
      this.emit();
      void g;
    }, ms);
    this.emit();
  }

  private clearSleep(): void {
    if (this.sleepTimer !== null) this.sched.clearTimeout(this.sleepTimer);
    if (this.sleepFadeTimer !== null) this.sched.clearTimeout(this.sleepFadeTimer);
    this.sleepTimer = null;
    this.sleepFadeTimer = null;
    this.sleepEndsAt = null;
  }

  /** Undo any sleep fade when the listener interacts. */
  private restoreFromSleep(): void {
    if (this.sleepFading || this.sleepEndsAt === null) {
      if (this.sleepFading) this.clearSleep();
      this.sleepFading = false;
      this.hooks.fade?.({ music: 1, env: 1 }, 1.5);
    }
  }

  // ----- internals -------------------------------------------------------------------
  private get hasLoadedTrack(): boolean {
    return this.tracks.length > 0;
  }

  private currentTrack(): TrackInfo | null {
    const i = this.order[this.pos];
    return i === undefined ? null : this.tracks[i];
  }

  private advanceIndex(delta: number): boolean {
    const n = this.order.length;
    let p = this.pos + delta;
    if (p >= n) {
      if (this.repeat === 'off' && delta > 0) return false;
      if (this.shuffle) this.buildOrder(-1);
      p = 0;
    } else if (p < 0) {
      p = n - 1;
    }
    this.pos = p;
    return true;
  }

  private advance(delta: number, manual: boolean): void {
    if (!this.tracks.length) return;
    this.pendingAdvance = false;
    if (manual) {
      // Manual skips always wrap around the queue.
      const n = this.order.length;
      const p = this.pos + delta;
      if (p >= n && this.shuffle) this.buildOrder(-1);
      this.pos = ((p % n) + n) % n;
    } else if (!this.advanceIndex(delta)) {
      this.status = 'ended';
      this.emit();
      return;
    }
    // Manual skips while paused keep the player paused (load only).
    if (manual && this.status === 'paused') {
      this.media.load(this.currentTrack()!.url);
      this.emit();
      return;
    }
    this.startTrack();
  }

  private startTrack(): void {
    const t = this.currentTrack();
    if (!t) return;
    this.gen++;
    const g = this.gen;
    this.status = 'loading';
    this.error = null;
    this.media.load(t.url);
    this.emit();
    this.playMedia(g);
  }

  private playMedia(g: number): void {
    this.media.play().then(
      () => {
        if (g !== this.gen || this.disposed) return;
        this.status = 'playing';
        this.emit();
      },
      (err: unknown) => {
        if (g !== this.gen || this.disposed) return;
        // Autoplay restrictions or decode failure: settle into a paused state, never loop.
        this.status = 'paused';
        this.error = err instanceof Error && err.name === 'NotAllowedError' ? 'blocked' : 'unavailable';
        this.emit();
      },
    );
  }

  private handleEnded(): void {
    if (this.disposed || this.status !== 'playing') return;
    if (this.repeat === 'one') {
      this.media.seek(0);
      this.gen++;
      this.playMedia(this.gen);
      return;
    }
    const isLast = this.pos >= this.order.length - 1;
    if (isLast && this.repeat === 'off') {
      this.status = 'ended';
      this.emit();
      return;
    }
    if (!this.ambientBreaks) {
      this.advance(1, false);
      return;
    }
    this.gen++;
    const g = this.gen;
    const ms = BREAK_MIN_MS + this.random() * (BREAK_MAX_MS - BREAK_MIN_MS);
    this.status = 'break';
    this.breakEndsAt = this.sched.now() + ms;
    this.breakTimer = this.sched.setTimeout(() => {
      this.breakTimer = null;
      if (g !== this.gen || this.disposed || this.status !== 'break') return;
      this.breakEndsAt = null;
      this.advance(1, false);
    }, ms);
    this.emit();
  }

  private handleError(): void {
    if (this.disposed) return;
    this.error = 'unavailable';
    // Skip a broken file after a short pause rather than stalling silently (only while active).
    if (this.status === 'playing' || this.status === 'loading') {
      this.gen++;
      const g = this.gen;
      this.status = 'paused';
      this.emit();
      this.sched.setTimeout(() => {
        if (g !== this.gen || this.disposed) return;
        this.advance(1, false);
      }, 3000);
      return;
    }
    this.emit();
  }

  private cancelBreak(): void {
    if (this.breakTimer !== null) this.sched.clearTimeout(this.breakTimer);
    this.breakTimer = null;
    this.breakEndsAt = null;
    if (this.status === 'break') this.status = 'paused';
  }

  snapshot(): PlayerSnapshot {
    const queue = this.order.map((i) => this.tracks[i]);
    let nextIndex = this.pos + 1;
    if (nextIndex >= queue.length) nextIndex = this.repeat === 'off' ? -1 : 0;
    return {
      status: this.status,
      track: this.currentTrack(),
      index: this.pos,
      queue,
      nextIndex,
      ambientBreaks: this.ambientBreaks,
      shuffle: this.shuffle,
      repeat: this.repeat,
      breakEndsAt: this.breakEndsAt,
      sleepEndsAt: this.sleepEndsAt,
      sleepFading: this.sleepFading,
      sleepMusicOnly: this.sleepMusicOnly,
      error: this.error,
    };
  }

  private emit(): void {
    this.hooks.onChange?.(this.snapshot());
  }

  dispose(): void {
    this.disposed = true;
    this.gen++;
    this.cancelBreak();
    this.clearSleep();
    this.media.pause();
    this.media.onEnded = null;
    this.media.onError = null;
  }
}
